import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { AuthService } from '../../modules/auth/auth.service';

const AUTH_KEY = 'ims-auth';

interface StoredAuth {
  email?: string;
  pin?: string;
  token?: string;
}

function readAuth(): StoredAuth | null {
  const raw = sessionStorage.getItem(AUTH_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as StoredAuth;
  } catch {
    return null;
  }
}

/** Attaches JWT + email/pin query params expected by IMS.Gateway. */
export const apiCredentialsInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const auth = readAuth();

  let headers = req.headers;
  if (auth?.token) {
    headers = headers.set('Authorization', `Bearer ${auth.token}`);
  }

  const isApiCall = req.url.includes('/api/') || req.url.startsWith('/api');
  const isAuthCall = /\/api\/Auth\//i.test(req.url);

  let outgoing = req.clone({ headers });

  if (isApiCall && !isAuthCall && auth?.email && auth?.pin) {
    let params = req.params;
    if (!params.has('email')) {
      params = params.set('email', auth.email);
    }
    if (!params.has('pin')) {
      params = params.set('pin', auth.pin);
    }
    outgoing = req.clone({ headers, params });
  }

  return next(outgoing).pipe(
    catchError((err: unknown) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        isApiCall &&
        !isAuthCall &&
        authService.isAuthenticated()
      ) {
        const headerReason = err.headers.get('X-Session-Reason')?.toLowerCase();
        const bodyText = JSON.stringify(err.error ?? '').toLowerCase();
        const reason =
          headerReason === 'replaced' || bodyText.includes('another device')
            ? 'replaced'
            : 'expired';

        authService.clearLocalSession();
        void router.navigate(['/login'], {
          queryParams: { reason },
        });
      }

      return throwError(() => err);
    }),
  );
};
